import React, { createContext, useContext, useState } from "react";
import { motion } from "framer-motion";

/* ---------------------------------------------------------------- */
/* Lead modal context — any CTA can open the lead capture form      */
/* ---------------------------------------------------------------- */
const LeadContext = createContext({ open: false, openLead: () => {}, closeLead: () => {} });
export const useLead = () => useContext(LeadContext);

export const LeadProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openLead = () => setOpen(true);
  const closeLead = () => setOpen(false);
  return (
    <LeadContext.Provider value={{ open, openLead, closeLead }}>
      {children}
    </LeadContext.Provider>
  );
};

/* ---------------------------------------------------------------- */
/* Smooth scroll to an element id                                   */
/* ---------------------------------------------------------------- */
export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const y = el.getBoundingClientRect().top + window.scrollY - 70;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

/* ---------------------------------------------------------------- */
/* Reveal — scroll entrance animation                               */
/* ---------------------------------------------------------------- */
export const Reveal = ({ children, delay = 0, y = 36, className = "", ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

/* ---------------------------------------------------------------- */
/* Section wrapper — consistent padding + max width                 */
/* ---------------------------------------------------------------- */
export const Section = ({ id, bg = "bg-brand-cream", className = "", children, dark = false }) => (
  <section
    id={id}
    className={`${bg} ${dark ? "text-brand-cream" : "text-brand-ink"} py-16 sm:py-20 md:py-28 lg:py-32 px-5 sm:px-8 md:px-12 ${className}`}
  >
    <div className="max-w-[1200px] xl:max-w-[1320px] mx-auto">{children}</div>
  </section>
);

/* ---------------------------------------------------------------- */
/* Eyebrow label pill                                               */
/* ---------------------------------------------------------------- */
export const Eyebrow = ({ children, color = "#0B31EB", dark = false }) => (
  <span
    className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em]"
    style={{
      borderColor: color,
      color: dark ? "#F2F1E5" : color,
      background: dark ? "transparent" : "#FFFFFF",
    }}
  >
    <span className="h-2 w-2 rounded-full" style={{ background: color }} />
    {children}
  </span>
);

/* ---------------------------------------------------------------- */
/* Brutal button                                                    */
/* ---------------------------------------------------------------- */
export const BrutalButton = ({
  children,
  onClick,
  href,
  as,
  variant = "primary",
  className = "",
  full = false,
  ...rest
}) => {
  const variants = {
    primary: "bg-brand-red text-white border-brand-black hover:shadow-[8px_10px_0_#111111]",
    red: "bg-brand-red text-white border-brand-black hover:shadow-[8px_10px_0_#111111]",
    blue: "bg-brand-blue text-white border-brand-black hover:shadow-[8px_10px_0_#111111]",
    yellow: "bg-brand-yellow text-brand-black border-brand-black hover:shadow-[8px_10px_0_#111111]",
    green: "bg-brand-green text-white border-brand-black hover:shadow-[8px_10px_0_#111111]",
    dark: "bg-brand-black text-white border-brand-black hover:shadow-[8px_10px_0_#0B31EB]",
    outline: "bg-transparent text-brand-black border-brand-black hover:bg-brand-black hover:text-white",
    white: "bg-white text-brand-black border-brand-black hover:shadow-[8px_10px_0_#111111]",
  };
  const base = `inline-flex items-center justify-center gap-2 border-2 rounded-[14px] px-6 sm:px-8 py-3.5 font-bold text-sm sm:text-base min-h-[52px] transition-[transform,box-shadow,background-color,color] duration-200 active:translate-x-[2px] active:translate-y-[2px] ${
    full ? "w-full" : ""
  } ${variants[variant]} ${className}`;

  const motionProps = { whileTap: { scale: 0.97 } };

  if (href) {
    return (
      <motion.a href={href} onClick={onClick} className={base} {...motionProps} {...rest}>
        {children}
      </motion.a>
    );
  }
  return (
    <motion.button type={as === "submit" ? "submit" : "button"} onClick={onClick} className={base} {...motionProps} {...rest}>
      {children}
    </motion.button>
  );
};

/* ---------------------------------------------------------------- */
/* Colored keyword helper for headlines                             */
/* ---------------------------------------------------------------- */
export const KW = ({ children, c }) => <span style={{ color: c }}>{children}</span>;
